import React, { useState, useEffect, useRef } from 'react';
import TextEdit from '../../../../molecules/TextEdit';
import { Auth } from 'aws-amplify';
import { isContractDisabled } from '../../utils/formatters/contractStatus';
import appConfig from '../../../../../config/app-config';

const { address: PERSON_API_URL, key: PERSON_API_KEY } = appConfig.apis.personApiEnv;
const { address: GEOLOCATION_API_URL, key: GEOLOCATION_API_KEY } = appConfig.apis.geolocationApiEnv;

export interface AssignorBillingAddressData {
    name: string;
    document_number: string;
    address_street: string;
    address_number: string;
    address_complement: string;
    address_neighborhood: string;
    address_city: string;
    address_state: string;
    address_zip_code: string;
}

export const EMPTY_ASSIGNOR_BILLING_ADDRESS: AssignorBillingAddressData = {
    name: '',
    document_number: '',
    address_street: '',
    address_number: '',
    address_complement: '',
    address_neighborhood: '',
    address_city: '',
    address_state: '',
    address_zip_code: '',
};

/**
 * Verifica se o endereço de faturamento está completamente preenchido.
 * Todos os campos são obrigatórios, exceto `address_complement`.
 */
export const isBillingAddressComplete = (address?: AssignorBillingAddressData | null): boolean => {
    if (!address) return false;
    return Boolean(
        address.name?.trim() &&
        address.document_number?.trim() &&
        address.address_street?.trim() &&
        address.address_number?.trim() &&
        address.address_neighborhood?.trim() &&
        address.address_city?.trim() &&
        address.address_state?.trim() &&
        address.address_zip_code?.trim()
    );
};

interface Props {
    value: AssignorBillingAddressData;
    onChange: (data: AssignorBillingAddressData) => void;
    disabled?: boolean;
    assignorPersonId?: string;
    assignorName?: string;
    assignorDocument?: string;
    contractStatus?: string;
}

const AssignorBillingAddress: React.FC<Props> = ({ value, onChange, assignorPersonId, assignorName, assignorDocument, contractStatus }) => {
    const [loadingAddress, setLoadingAddress] = useState(false);
    const lastFetchedId = useRef<string>('');
    const valueRef = useRef(value);
    valueRef.current = value;

    // Pre-fill name and document from assignor props
    useEffect(() => {
        if (!assignorName && !assignorDocument) return;
        const current = valueRef.current;
        const needsUpdate = (assignorName && current.name !== assignorName) || (assignorDocument && current.document_number !== assignorDocument);
        if (!needsUpdate) return;
        onChange({ ...current, name: assignorName || current.name, document_number: assignorDocument || current.document_number });
    }, [assignorName, assignorDocument]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch address from Person API when contract is not finalized
    useEffect(() => {
        if (!assignorPersonId || assignorPersonId === '00000000-0000-0000-0000-000000000000') return;
        if (isContractDisabled(contractStatus)) return;
        if (!valueRef.current.name) return;
        if (lastFetchedId.current === assignorPersonId) return;

        lastFetchedId.current = assignorPersonId;
        const fetchAssignorAddress = async () => {
            setLoadingAddress(true);
            try {
                const token = await Auth.currentSession().then(s => s.getIdToken().getJwtToken());
                const res = await fetch(`${PERSON_API_URL}/person/${assignorPersonId}/addresses`, {
                    headers: { 'x-api-key': PERSON_API_KEY, 'Authorization': `Bearer ${token}` }
                });
                if (!res.ok) return;
                const data = await res.json();
                const addresses = data.addresses || data || [];
                if (addresses.length === 0) return;
                const addr = addresses[0];

                let cityName = addr.city || '';
                let stateName = addr.state || addr.province || '';

                if (addr.cityId && (!cityName || !stateName)) {
                    try {
                        const cityRes = await fetch(`${GEOLOCATION_API_URL}/cities/${addr.cityId}?fields=id,name,province`, {
                            headers: { 'x-api-key': GEOLOCATION_API_KEY }
                        });
                        if (cityRes.ok) {
                            const cityData = await cityRes.json();
                            if (!cityName) cityName = cityData.name || '';
                            if (!stateName) stateName = cityData.province?.initials || cityData.province?.abbreviation || cityData.province || '';
                        }
                    } catch (error: unknown) { void error; }
                }

                onChange({
                    name: assignorName || valueRef.current.name,
                    document_number: assignorDocument || valueRef.current.document_number,
                    address_street: addr.street || '',
                    address_number: addr.number || '',
                    address_complement: addr.complement || '',
                    address_neighborhood: addr.district || addr.neighborhood || '',
                    address_city: cityName,
                    address_state: stateName,
                    address_zip_code: addr.zipcode || addr.zip_code || '',
                });
            } catch (error: unknown) { void error; }
            finally { setLoadingAddress(false); }
        };
        fetchAssignorAddress();
    }, [assignorPersonId, contractStatus, value.name, onChange, assignorName, assignorDocument]);

    return (
        <div className="border border-gray-200 dark:border-slate-600 rounded-lg p-4 mt-4">
            <h3 className="text-md font-semibold mb-3 text-gray-700 dark:text-gray-200">
                Endereço de Faturamento
            </h3>
            {loadingAddress ? (
                <div className="flex items-center justify-center py-8 gap-3">
                    <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    <span className="text-sm text-blue-500 dark:text-blue-400">Localizando endereço...</span>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <TextEdit id="billing-name" label="Nome / Razão Social *" value={value.name} disabled={true} />
                    <TextEdit id="billing-document" label="CPF / CNPJ *" value={value.document_number} disabled={true} />
                    <TextEdit id="billing-zip" label="CEP *" value={value.address_zip_code} disabled={true} />
                    <TextEdit id="billing-street" label="Logradouro *" value={value.address_street} disabled={true} />
                    <TextEdit id="billing-number" label="Número *" value={value.address_number} disabled={true} />
                    <TextEdit id="billing-complement" label="Complemento" value={value.address_complement} disabled={true} />
                    <TextEdit id="billing-neighborhood" label="Bairro *" value={value.address_neighborhood} disabled={true} />
                    <TextEdit id="billing-city" label="Cidade *" value={value.address_city} disabled={true} />
                    <TextEdit id="billing-state" label="UF *" value={value.address_state} disabled={true} maxLength={2} />
                </div>
            )}
        </div>
    );
};

export default AssignorBillingAddress;
